import { useCallback, useEffect, useState } from 'react';
import type { FeatureEntitlement } from '@alltrails/types/FeatureEntitlement';
import logError from '../utils/logError';

type Args = {
  featureEntitlement: FeatureEntitlement;
  maxNumberOfUserInteractions?: number;
  // Invoked when the preview expires due to a timer, cancellation, the maximum number of interactions being reached, etc.
  onPreviewFinished: () => void;
};

export default function useFeaturePreview({ featureEntitlement, maxNumberOfUserInteractions, onPreviewFinished }: Args) {
  const [isPreviewActive, setIsPreviewActive] = useState(false);
  // Whether the preview is used up by a timer having expired, user interactions limit being reached, explicit cancellation, etc.
  const [isPreviewExhausted, setIsPreviewExhausted] = useState(false);

  const onPreviewFinishedInternal = useCallback(() => {
    setIsPreviewActive(false);
    setIsPreviewExhausted(true);
    onPreviewFinished();
  }, [onPreviewFinished]);

  // Add more hooks as needed for per-feature checks that need to be handled for previews.
  const { setTokenExpirationDuration } = usePreviewTimer({ onPreviewFinished: onPreviewFinishedInternal, isPreviewExhausted });
  const { trackUserInteractions } = useInteractionTracker({
    onPreviewFinished: onPreviewFinishedInternal,
    isPreviewExhausted,
    maxNumberOfUserInteractions
  });

  // requestPreview handles state related to the API request for feature preview entitlements. Handle all the behaviors that would apply to any preview request here.
  const requestPreview = useCallback(async () => {
    // Don't bother making the network request. Either the preview ran its course or the user canceled it. As far as we're concerned it's finished.
    if (isPreviewExhausted) {
      return { isEligible: false };
    }

    // The API expects an array of entitlements. At time of writing, the web client should only ever request one at a time.
    const { isEligible, expiresAt } = await requestPreviewInternal([featureEntitlement]);

    if (isEligible) {
      setTokenExpirationDuration(expiresAt);
      setIsPreviewActive(true);
    }

    return { isEligible };
  }, [isPreviewExhausted, setTokenExpirationDuration, featureEntitlement]);

  return {
    isPreviewActive,
    requestPreview,
    cancelPreview: onPreviewFinishedInternal, // If we need additional logic specific to cancellations, we can make a callback just for that when we need it.
    trackUserInteractions
  };
}

// usePreviewTimer tracks a timeout and invokes a function when the timer runs out.
function usePreviewTimer({ onPreviewFinished, isPreviewExhausted }: { onPreviewFinished: Args['onPreviewFinished']; isPreviewExhausted: boolean }) {
  const [tokenExpirationDuration, setTokenExpirationDuration] = useState<number>();

  useEffect(() => {
    if (tokenExpirationDuration === undefined || isPreviewExhausted) {
      return;
    }

    const timeoutMs = Math.max(tokenExpirationDuration - Date.now(), 0);

    const timer = setTimeout(() => {
      console.log(`[${new Date().toISOString()}] useFeaturePreview: expired`);
      onPreviewFinished();
    }, timeoutMs);

    console.log(`[${new Date().toISOString()}] useFeaturePreview: expires at ${new Date(tokenExpirationDuration).toISOString()} in ${timeoutMs}ms`);

    return () => {
      clearTimeout(timer);
    };
  }, [onPreviewFinished, tokenExpirationDuration, isPreviewExhausted]);

  return { setTokenExpirationDuration };
}

// useInteractionTracker tracks user-driven interactions (clicks, API requests, etc.) and invokes a function when the maximum limit is reached.
function useInteractionTracker({
  onPreviewFinished,
  isPreviewExhausted,
  maxNumberOfUserInteractions
}: Pick<Args, 'onPreviewFinished' | 'maxNumberOfUserInteractions'> & {
  isPreviewExhausted: boolean;
}) {
  const [numberOfInteractionsObserved, setNumberOfInteractionsObserved] = useState(0);

  const trackUserInteractions = useCallback(() => {
    if (isPreviewExhausted || !maxNumberOfUserInteractions) {
      return;
    }

    setNumberOfInteractionsObserved(n => n + 1);
  }, [isPreviewExhausted, maxNumberOfUserInteractions]);

  useEffect(() => {
    if (!maxNumberOfUserInteractions) {
      return;
    }

    console.log(
      `[${new Date().toISOString()}] useFeaturePreview: tracked ${numberOfInteractionsObserved} of ${maxNumberOfUserInteractions} interactions`
    );

    if (numberOfInteractionsObserved >= maxNumberOfUserInteractions) {
      onPreviewFinished();
    }
  }, [numberOfInteractionsObserved, maxNumberOfUserInteractions, onPreviewFinished]);

  return { trackUserInteractions };
}

// requestPreviewInternal is stateless and handles the API logic for requesting feature preview entitlements.
async function requestPreviewInternal(featureEntitlements: FeatureEntitlement[]) {
  try {
    const res = await fetch('/api/alltrails/feature_entitlements/previews', {
      headers: {
        'content-type': 'application/json',
        'x-at-key': process.env.NEXT_PUBLIC_API_KEY!
      },
      method: 'POST',
      body: JSON.stringify({
        feature_entitlements: featureEntitlements
      })
    });

    const isPreviewUnavailable = res.status === 402;
    if (isPreviewUnavailable || !res.ok) {
      return { isEligible: false };
    }

    const json = await res.json();

    const expiresAt = Date.parse(json['expires_at'] as string);

    // 'expires_at' is a string formatted ISO8601 date like '2025-06-27T21:21:39'. We return a parsed JS epoch timestamp.
    // TODO As a hint to the client, should the server also send max_interactions and similar limits that may be helpful?
    return { isEligible: true, expiresAt };
  } catch (error) {
    logError(new Error('Error retrieving feature preview entitlements', { cause: error }));

    return { isEligible: false };
  }
}
